/**
 * Product card cert flip — collection grid vertical cards.
 * Native stretched link handles PDP navigation; this module manages flip state,
 * cert badge activation, and keyboard/focus behavior.
 */
(function () {
  'use strict';

  if (window.productCardCertFlip) return;

  const BADGE_SELECTOR = '.cert-badge:not(.cert-badge--inactive)';
  const CARD_BAR_SELECTOR = '.cert-badges-bar--card';
  const WRAPPER_SELECTOR = '.product-card-wrapper';
  const FLIP_SCENE_SELECTOR = '.product-card-wrapper.product-card__flip-scene';
  const FLIPPED_CLASS = 'is-cert-flipped';
  const INTERACTIVE_SELECTOR =
    '.product-card__cta, .quick-add, product-form, modal-opener, quantity-popover, .cert-card-back__close';
  const CERT_CLASSES = ['cert-badge--peta', 'cert-badge--made-safe', 'cert-badge--ewg', 'cert-badge--skinsafe'];
  const CERT_KEYS = ['peta', 'made-safe', 'ewg', 'skinsafe'];

  function certKeyFromBadge(badge) {
    for (let i = 0; i < CERT_CLASSES.length; i += 1) {
      if (badge.classList.contains(CERT_CLASSES[i])) return CERT_KEYS[i];
    }
    return null;
  }

  function wrapperForBadge(badge) {
    return badge.closest(WRAPPER_SELECTOR);
  }

  function faceEls(wrapper) {
    return {
      front: wrapper.querySelector('.product-card__face--front'),
      back: wrapper.querySelector('.product-card__face--back'),
    };
  }

  function panels(wrapper) {
    return wrapper.querySelectorAll('[data-cert-panel]');
  }

  function activePanel(wrapper, certKey) {
    return wrapper.querySelector(`[data-cert-panel="${certKey}"]`);
  }

  function setFaceVisibility(wrapper, flipped) {
    const { front, back } = faceEls(wrapper);
    if (front) {
      front.setAttribute('aria-hidden', flipped ? 'true' : 'false');
      if (flipped) front.setAttribute('inert', '');
      else front.removeAttribute('inert');
    }
    if (back) {
      back.setAttribute('aria-hidden', flipped ? 'false' : 'true');
      if (flipped) back.removeAttribute('inert');
      else back.setAttribute('inert', '');
    }
  }

  function dismissBadgeTips(wrapper) {
    wrapper.querySelectorAll(BADGE_SELECTOR).forEach((badge) => {
      badge.classList.remove('is-open');
      badge.setAttribute('aria-expanded', 'false');
      const tip = badge.querySelector('.cert-badge__tip');
      if (tip) tip.hidden = true;
    });
  }

  function activatePanel(wrapper, certKey) {
    panels(wrapper).forEach((panel) => {
      panel.classList.toggle('is-active', panel.dataset.certPanel === certKey);
    });
  }

  function closeFlip(wrapper) {
    if (!wrapper || !wrapper.classList.contains(FLIPPED_CLASS)) return;

    window.productCardTilt?.onFlipStart?.(wrapper);
    dismissBadgeTips(wrapper);

    const activeElement = document.activeElement;
    if (activeElement instanceof HTMLElement && wrapper.contains(activeElement)) {
      activeElement.blur();
    }

    wrapper.classList.remove(FLIPPED_CLASS);
    wrapper.removeAttribute('data-cert-active');
    panels(wrapper).forEach((panel) => panel.classList.remove('is-active'));
    setFaceVisibility(wrapper, false);
  }

  function openFlip(wrapper, certKey) {
    const panel = activePanel(wrapper, certKey);
    if (!panel) return;

    window.productCardTilt?.onFlipStart?.(wrapper);
    dismissBadgeTips(wrapper);
    activatePanel(wrapper, certKey);
    wrapper.classList.add(FLIPPED_CLASS);
    wrapper.setAttribute('data-cert-active', certKey);
    setFaceVisibility(wrapper, true);

    const closeBtn = wrapper.querySelector('.cert-card-back__close');
    if (closeBtn) closeBtn.focus();
  }

  function flipToCert(badge, event) {
    const bar = badge.closest(CARD_BAR_SELECTOR);
    if (!bar) return false;

    const wrapper = wrapperForBadge(badge);
    if (!wrapper) return false;

    const certKey = certKeyFromBadge(badge);
    if (!certKey) return false;

    const alreadyFlipped = wrapper.classList.contains(FLIPPED_CLASS);
    const sameCert = wrapper.getAttribute('data-cert-active') === certKey;

    document.querySelectorAll(`${WRAPPER_SELECTOR}.${FLIPPED_CLASS}`).forEach((other) => {
      if (other !== wrapper) closeFlip(other);
    });

    if (alreadyFlipped && sameCert) {
      closeFlip(wrapper);
      return true;
    }

    openFlip(wrapper, certKey);
    if (event && event.detail === 0 && badge instanceof HTMLElement) {
      badge.focus();
    }
    return true;
  }

  function onBadgeActivate(event) {
    const badge = event.target.closest(`${CARD_BAR_SELECTOR} ${BADGE_SELECTOR}`);
    if (!badge) return;

    const wrapper = wrapperForBadge(badge);
    if (!wrapper || wrapper.classList.contains('is-flip-animating')) return;

    if (flipToCert(badge, event)) {
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
    }
  }

  function onCardClick(event) {
    const wrapper = event.target.closest(FLIP_SCENE_SELECTOR);
    if (!wrapper || wrapper.classList.contains('is-flip-animating')) return;

    if (event.target.closest(INTERACTIVE_SELECTOR)) return;

    if (event.target.closest(`${CARD_BAR_SELECTOR} ${BADGE_SELECTOR}`)) {
      const badge = event.target.closest('.cert-badge');
      if (badge && flipToCert(badge, event)) {
        event.preventDefault();
        event.stopImmediatePropagation();
      }
      return;
    }

    if (wrapper.classList.contains(FLIPPED_CLASS)) {
      event.preventDefault();
      closeFlip(wrapper);
      return;
    }

    if (event.target.closest('.product-card__face--front a[href]')) return;
  }

  function onOutsideClick(event) {
    if (event.target.closest(WRAPPER_SELECTOR)) return;
    document.querySelectorAll(`${WRAPPER_SELECTOR}.${FLIPPED_CLASS}`).forEach(closeFlip);
  }

  document.addEventListener('click', onBadgeActivate, true);
  document.addEventListener('click', onCardClick);
  document.addEventListener('click', onOutsideClick);

  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Enter' && event.key !== ' ') return;
    const badge = event.target.closest(`${CARD_BAR_SELECTOR} ${BADGE_SELECTOR}`);
    if (!badge) return;
    onBadgeActivate(event);
  });

  document.addEventListener('mouseout', (event) => {
    const wrapper = event.target.closest(`${WRAPPER_SELECTOR}.${FLIPPED_CLASS}`);
    if (!wrapper) return;

    const related = event.relatedTarget;
    if (related instanceof Node && wrapper.contains(related)) return;

    closeFlip(wrapper);
  });

  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') return;
    document.querySelectorAll(`${WRAPPER_SELECTOR}.${FLIPPED_CLASS}`).forEach(closeFlip);
  });

  window.productCardCertFlip = {
    flipToCert,
    close: closeFlip,
    closeAll() {
      document.querySelectorAll(`${WRAPPER_SELECTOR}.${FLIPPED_CLASS}`).forEach(closeFlip);
    },
    isCardGridBadge(badge) {
      return Boolean(badge && badge.closest(CARD_BAR_SELECTOR));
    },
  };
})();
